import { createSelector } from '@reduxjs/toolkit'
import { type PersistedResourceIdentifier } from '@opallabs/json-api'
import { getNestedProperty } from '@opallabs/lambda'
import getCollectionOrEmptyObject from '@opallabs/lambda/src/GetCollectionOrEmptyObject'
import {
  StoryCollection,
  Moment,
  ServiceCollection,
  AssetReferenceCollection,
  ContentCollection,
  BrandCollection,
} from '../../../../types'
import { getAssetReferences } from '../../getters/entities/assetReference'
import { getContent } from '../../getters/entities/content'
import { getServices } from '../../getters/entities/services'
import { getStories } from '../../getters/entities/stories'
import { selectBrands } from './brands'
import { selectMomentById } from './moments'

const selectStories = createSelector([getStories], (stories) =>
  getCollectionOrEmptyObject(stories)
)

const selectContent = createSelector([getContent], (content) =>
  getCollectionOrEmptyObject(content)
)

const selectServices = createSelector([getServices], (services) =>
  getCollectionOrEmptyObject(services)
)

const selectAssetReferences = createSelector(
  [getAssetReferences],
  (assetReferences) => getCollectionOrEmptyObject(assetReferences)
)

export interface MomentPreviewData {
  coverImageUuid?: string
  momentName: string
  storyColor?: string
  storyName?: string
  momentStartDate?: Date
  momentEndDate?: Date
  channelList?: string[]
  storyDueDate?: Date
  brandSlug?: string
}

const normalizeMomentPreviewData = (
  moment: Moment | undefined,
  stories: StoryCollection,
  content: ContentCollection,
  services: ServiceCollection,
  brands: BrandCollection,
  assetReferences: AssetReferenceCollection
): MomentPreviewData => {
  if (moment !== undefined) {
    const momentData: MomentPreviewData = {
      momentName: moment.title,
    }
    momentData.momentStartDate = new Date(moment.scheduled_at)
    momentData.momentEndDate = moment.scheduled_end_at
      ? new Date(moment.scheduled_end_at)
      : undefined

    const storyId = getNestedProperty<string>(
      moment,
      'relationships',
      'stories',
      '0',
      'id'
    )
    if (storyId !== undefined) {
      const story = stories[storyId]
      momentData.storyName = story.name
      momentData.storyColor = story.color ? story.color : undefined
      momentData.storyDueDate = new Date(story.scheduled_end_at)
      if (momentData.momentEndDate === undefined) {
        momentData.momentEndDate = new Date(story.scheduled_end_at)
      }
    }

    const brandId = getNestedProperty<string>(
      moment,
      'relationships',
      'brand',
      'id'
    )
    if (brandId !== undefined) {
      const brand = brands[brandId]
      if (brand !== undefined) {
        momentData.brandSlug = brand.slug
      }
    }

    const momentPosts = getNestedProperty<PersistedResourceIdentifier[]>(
      moment,
      'relationships',
      'posts'
    )
    if (momentPosts !== undefined) {
      const channels = momentPosts.reduce<string[]>(
        (iconNames, postRelationship) => {
          const post = content[postRelationship.id]
          if (post !== undefined) {
            const serviceId = getNestedProperty<string>(
              post,
              'relationships',
              'service',
              'id'
            )
            if (serviceId !== undefined) {
              const service = services[serviceId]
              return [...iconNames, service.icon_name]
            }
          }
          return iconNames
        },
        []
      )

      // In JS we could just do [...new Set(channels)], this is a TS workaround
      momentData.channelList = Array.from(new Set(channels).values())
    }

    const primaryAssetReferenceId = getNestedProperty<string>(
      moment,
      'relationships',
      'primary_asset_reference',
      'id'
    )
    if (primaryAssetReferenceId !== undefined) {
      const asset = assetReferences[primaryAssetReferenceId]
      if (asset !== undefined) {
        momentData.coverImageUuid =
          asset.asset_uuid !== null ? asset.asset_uuid : undefined
      }
    }

    return momentData
  }
  return {
    momentName: 'Unable to retrieve Moment Data',
  }
}

export const selectMomentPreviewData = createSelector(
  [
    selectMomentById,
    selectStories,
    selectContent,
    selectServices,
    selectBrands,
    selectAssetReferences,
  ],
  (moment, stories, content, services, brands, assetReferences) =>
    normalizeMomentPreviewData(
      moment,
      stories,
      content,
      services,
      brands,
      assetReferences
    )
)
